import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import './CampReportModal.css';

interface Camp {
  id: string;
  name: string;
}

interface CampAttendee {
  enrollment_id: string;
  camp_id: string;
  camp_name: string;
  camper_name: string;
  camper_grade: string | null;
  parent_name: string;
  parent_phone: string | null;
  parent_email: string | null;
  camp_day_id: string | null;
  attendance_id: string | null;
  attendance_status: string | null;
  date: string | null;
}

interface CampAttendanceModalProps {
  onClose: () => void;
}

const CampAttendanceModal: React.FC<CampAttendanceModalProps> = ({ onClose }) => {
  const [camps, setCamps] = useState<Camp[]>([]);
  const [selectedCampIds, setSelectedCampIds] = useState<string[]>([]);
  const [attendees, setAttendees] = useState<CampAttendee[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [loadingCamps, setLoadingCamps] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    fetchCamps();
  }, []);

  useEffect(() => {
    if (selectedCampIds.length > 0) {
      fetchAttendees();
    } else {
      setAttendees([]);
    }
  }, [selectedCampIds, selectedDate]);

  const fetchCamps = async () => {
    try {
      setLoadingCamps(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps?status=published`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCamps(data);
      }
    } catch (error) {
      console.error('Error fetching camps:', error);
    } finally {
      setLoadingCamps(false);
    }
  };

  const fetchAttendees = async () => {
    if (selectedCampIds.length === 0) {
      setAttendees([]);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const campIdsParam = selectedCampIds.join(',');
      const url = `${config.BACKEND_URL}/api/camps/attendance/attendees?camp_ids=${campIdsParam}${selectedDate ? `&day_date=${selectedDate}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAttendees(data);
      } else {
        console.error('Failed to fetch attendees:', response.status);
        setAttendees([]);
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCampToggle = (campId: string) => {
    setSelectedCampIds(prev => {
      if (prev.includes(campId)) {
        return prev.filter(id => id !== campId);
      } else {
        return [...prev, campId];
      }
    });
  };

  const handleMarkAttendance = async (enrollmentId: string, campDayId: string | null, status: 'present' | 'absent') => {
    if (!campDayId) {
      alert('Please select a date first to mark attendance');
      return;
    }

    try {
      setSaving(enrollmentId);
      const token = localStorage.getItem('cognitoToken') || '';
      
      const response = await fetch(`${config.BACKEND_URL}/api/camps/attendance/mark`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enrollment_id: enrollmentId,
          camp_day_id: campDayId,
          status: status
        })
      });

      if (response.ok) {
        // Refresh attendees to get updated status
        await fetchAttendees();
      } else {
        const error = await response.json();
        alert(`Failed to mark attendance: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'present':
        return <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Present</span>;
      case 'absent':
        return <span style={{ color: '#dc3545', fontWeight: 'bold' }}>✗ Absent</span>;
      case 'excused_absence':
        return <span style={{ color: '#ffc107', fontWeight: 'bold' }}>⚠ Excused</span>;
      case 'late':
        return <span style={{ color: '#fd7e14', fontWeight: 'bold' }}>⏰ Late</span>;
      case 'early_departure':
        return <span style={{ color: '#6f42c1', fontWeight: 'bold' }}>↩ Early</span>;
      default:
        return <span style={{ color: '#6c757d' }}>Not Marked</span>;
    }
  };

  return (
    <div className="camp-report-modal-overlay" onClick={onClose}>
      <div className="camp-report-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', width: '95%' }}>
        <div className="camp-report-header">
          <h2>📋 Camp Attendance</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="camp-report-content">
          {/* Filters */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Select Camps (Multi-select):
              </label>
              {loadingCamps ? (
                <p>Loading camps...</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {camps.map(camp => (
                    <label
                      key={camp.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: selectedCampIds.includes(camp.id) ? '#667eea' : '#fff',
                        color: selectedCampIds.includes(camp.id) ? '#fff' : '#333',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCampIds.includes(camp.id)}
                        onChange={() => handleCampToggle(camp.id)}
                        style={{ marginRight: '8px' }}
                      />
                      {camp.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Date:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Attendees Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading attendees...</p>
            </div>
          ) : attendees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
              {selectedCampIds.length === 0 
                ? 'Please select at least one camp to view attendees'
                : 'No attendees found for the selected camps and date'}
            </div>
          ) : (
            <div className="camp-report-table-container">
              <table className="camp-report-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Camp</th>
                    <th>Camper Name</th>
                    <th>Grade</th>
                    <th>Parent Name</th>
                    <th>Parent Phone</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attendees.map((attendee) => (
                    <tr key={attendee.enrollment_id}>
                      <td>{attendee.camp_name}</td>
                      <td>{attendee.camper_name}</td>
                      <td>{attendee.camper_grade || 'N/A'}</td>
                      <td>{attendee.parent_name}</td>
                      <td>{attendee.parent_phone || 'N/A'}</td>
                      <td>{getStatusBadge(attendee.attendance_status)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: attendee.attendance_status === 'present' ? '#28a745' : '#6c757d',
                              color: '#fff',
                              padding: '4px 12px',
                              fontSize: '12px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: saving === attendee.enrollment_id ? 'not-allowed' : 'pointer',
                              opacity: saving === attendee.enrollment_id ? 0.6 : 1
                            }}
                            onClick={() => handleMarkAttendance(attendee.enrollment_id, attendee.camp_day_id, 'present')}
                            disabled={!attendee.camp_day_id || saving === attendee.enrollment_id}
                          >
                            {saving === attendee.enrollment_id ? 'Saving...' : 'Present'}
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: attendee.attendance_status === 'absent' ? '#dc3545' : '#6c757d',
                              color: '#fff',
                              padding: '4px 12px',
                              fontSize: '12px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: saving === attendee.enrollment_id ? 'not-allowed' : 'pointer',
                              opacity: saving === attendee.enrollment_id ? 0.6 : 1
                            }}
                            onClick={() => handleMarkAttendance(attendee.enrollment_id, attendee.camp_day_id, 'absent')}
                            disabled={!attendee.camp_day_id || saving === attendee.enrollment_id}
                          >
                            {saving === attendee.enrollment_id ? 'Saving...' : 'Absent'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="camp-report-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampAttendanceModal;


